import React, { useState, useCallback } from 'react';
import { BookingRequestsButton } from '@features/scheduling-settings';
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  useMediaQuery,
  useTheme,
  Fab,
  Zoom,
} from '@mui/material';
import { Button } from '@components/primitives/Button';
import { ChevronLeftIcon } from '@jotpsych/ui/icons/ChevronLeftIcon';
import { ChevronRightIcon } from '@jotpsych/ui/icons/ChevronRightIcon';
import { PlusIcon } from '@jotpsych/ui/icons/PlusIcon';
import { DownloadIcon } from '@jotpsych/ui/icons/DownloadIcon';
import { useAppointments } from '@features/appointments';
import { useReportsAccess } from '@features/reports/hooks/useReportsAccess';
import { useProviderBillingInfo, useUserName } from '@features/user';
import { useModal } from '@store/hooks';
import { MODAL_TYPES } from '@components/modals/constants';
import { useEventTracking } from '@services/statsig';
import { format } from 'date-fns';
import {
  calendarViewToReportWindow,
  providerFilterFromNames,
  reportClinicianName,
  reportWindowDays,
} from '../utils/scheduleReportSeed';
import AddAppointmentModal from './AddAppointmentModal';
import { useOrgScheduleFilters } from '../hooks/useOrgScheduleFilters';
import { useOrgScheduleAccess } from '../hooks/useOrgScheduleAccess';
import { useScheduleReadOnly } from '../hooks/useScheduleReadOnly';
import SyncGoogleCalendar from './SyncGoogleCalendar';
import CalendarSettingsMenu from './CalendarSettingsMenu';
import ScheduleViewToggle from './ScheduleViewToggle';
import ViewModeSelector from './ViewModeSelector';
import GroupBySelector from './GroupBySelector';
import { Appointment as AppointmentResponse, SeriesResponse } from '@features/appointments';

interface CalendarNavigationProps {
  // Optional additional props can be added here
}

const CalendarNavigation: React.FC<CalendarNavigationProps> = () => {
  const {
    viewMode,
    currentDate,
    setCurrentDate,
    requestScrollToNow,
    refreshAppointments,
    isSidebarOpen,
    setSidebarOpen,
    isRequestsPanelOpen,
    setRequestsPanelOpen,
  } = useAppointments();
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const { isOrgViewActive, selectedProviderIds } = useOrgScheduleFilters();
  const { hasOrgScheduleAccess } = useOrgScheduleAccess();
  const isOrgScheduleActive = hasOrgScheduleAccess && isOrgViewActive;
  const isReadOnly = useScheduleReadOnly();
  const { open: openModal } = useModal();
  const { hasAccess: hasReportsAccess } = useReportsAccess();
  const { reports } = useEventTracking();
  // The report's `clinician` column renders `first_name last_name` (falling back
  // to the legacy display name). We can build that only for the CURRENT user
  // (billing info + legacy name); other clinicians expose no first/last on the
  // frontend, so we can't pre-select them — see the Practice branch below.
  const billingInfo = useProviderBillingInfo();
  const currentUserLegacyName = useUserName();

  // Download button → open the schedule report seeded to the calendar's current
  // view. The date range is always seeded. The provider filter is seeded only in
  // My Schedule view (to the current user, whose report name we can build); in
  // Practice view it's dropped, because org-members expose only the legacy name,
  // which the report's first+last `clinician` column won't match — provider
  // scoping there awaits a backend change. Seeds are preselected but editable.
  const handleOpenScheduleReport = useCallback(() => {
    const initialWindow = calendarViewToReportWindow(viewMode, new Date(currentDate));
    const currentUserReportName = reportClinicianName({
      firstName: billingInfo?.first_name,
      lastName: billingInfo?.last_name,
      legacyName: currentUserLegacyName,
    });
    const initialFilters = isOrgScheduleActive
      ? []
      : providerFilterFromNames([currentUserReportName]);

    reports.downloadClicked({
      report_key: 'schedule',
      view_mode: viewMode,
      window_days: reportWindowDays(initialWindow),
      provider_filter_count: initialFilters[0]?.values?.length ?? 0,
    });

    openModal(MODAL_TYPES.SCHEDULE_REPORTS, {
      initialWindow,
      initialFilters,
      maxWidth: 'xl',
      fullHeight: true,
      fullScreenOnMobile: true,
    });
  }, [
    viewMode,
    currentDate,
    isOrgScheduleActive,
    billingInfo,
    currentUserLegacyName,
    reports,
    openModal,
  ]);

  // When the org-schedule view is active and the user has explicitly cleared
  // every provider (selectedProviderIds === [], distinct from null = "all"),
  // the calendar shows nothing — so there's nothing meaningful to download.
  // Disable the button rather than open a report that would misleadingly show
  // every provider (the report can't express an empty-selection filter).
  const isScheduleReportDisabled =
    isOrgScheduleActive &&
    Array.isArray(selectedProviderIds) &&
    selectedProviderIds.length === 0;

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Handle navigation functions
  const handlePrevious = () => {
    const newDate = new Date(currentDate);
    if (viewMode === 'day') {
      newDate.setDate(newDate.getDate() - 1);
    } else if (viewMode === 'week') {
      newDate.setDate(newDate.getDate() - 7);
    } else if (viewMode === 'month') {
      newDate.setMonth(newDate.getMonth() - 1);
    }
    setCurrentDate(newDate.toISOString());
  };

  const handleNext = () => {
    const newDate = new Date(currentDate);
    if (viewMode === 'day') {
      newDate.setDate(newDate.getDate() + 1);
    } else if (viewMode === 'week') {
      newDate.setDate(newDate.getDate() + 7);
    } else if (viewMode === 'month') {
      newDate.setMonth(newDate.getMonth() + 1);
    }
    setCurrentDate(newDate.toISOString());
  };

  const handleToday = () => {
    setCurrentDate(new Date().toISOString());
    requestScrollToNow();
  };

  // Get appropriate date format based on view mode
  const getDateDisplay = () => {
    if (viewMode === 'day') {
      return format(currentDate, 'MMMM d, yyyy');
    } else if (viewMode === 'week') {
      const startOfWeek = new Date(currentDate);
      const day = startOfWeek.getDay();
      // Sunday is 0, so we subtract the day number to get to Sunday
      startOfWeek.setDate(startOfWeek.getDate() - day);

      const endOfWeek = new Date(startOfWeek);
      endOfWeek.setDate(endOfWeek.getDate() + 6);

      return `${format(startOfWeek, 'MMM d')} - ${format(endOfWeek, 'MMM d, yyyy')}`;
    } else {
      return format(currentDate, 'MMMM yyyy');
    }
  };

  // Handle encounter creation callback
  const handleAppointmentCreated = useCallback(
    (appointment: AppointmentResponse | SeriesResponse) => {
      console.log(
        `New ${appointment.series_id ? 'series' : 'appointment'} created with ID: ${
          'series_id' in appointment ? appointment.series_id : appointment.appointment_id
        }`,
      );
      // Refresh encounters to show the new one
      refreshAppointments();
    },
    [refreshAppointments],
  );

  return (
    <Box
      sx={{
        p: 3,
        display: 'grid',
        gridTemplateColumns: {
          xs: 'auto 1fr', // Two columns on mobile for nav and left in same row
          md: '216px 1fr auto', // Three columns for two-row layout
          lg: '216px minmax(342px, 1fr) auto minmax(auto, 1fr)', // Four zones on large screens
        },
        gridTemplateRows: {
          xs: 'auto', // Three rows on mobile
          md: 'auto auto', // Two rows for md breakpoint
          lg: 'auto', // Single row on large screens
        },
        gridTemplateAreas: {
          xs: '"nav left"', // Nav and left in top row on mobile
          md: '"nav left right" "nav center center"', // Two rows: nav spans both
          lg: '"nav left center right"', // True four-column layout
        },
        columnGap: 4,
        rowGap: 2,
        alignItems: 'flex-start',
      }}
    >
      {/* Nav Section - New Appointment Button */}
      <Box
        sx={{
          gridArea: 'nav',
          display: 'flex',
          alignItems: 'center',
          gap: 2,
        }}
      >
        {/* Sidebar toggle for mobile */}
        {isMobile && (
          <IconButton
            onClick={() => setSidebarOpen(!isSidebarOpen)}
            size="large"
            edge="start"
            sx={{
              color: '#535976',
              display: { xs: 'flex', md: 'none' },
              padding: 1,
            }}
            aria-label={isSidebarOpen ? 'Close calendar sidebar' : 'Open calendar sidebar'}
            aria-expanded={isSidebarOpen}
          >
            {isSidebarOpen ? <ChevronLeftIcon /> : <ChevronRightIcon />}
          </IconButton>
        )}

        {/* New Appointment button - only on desktop, hidden in read-only mode */}
        {!isMobile && !isReadOnly && (
          <Button
            variant="outlined"
            color="primary"
            startIcon={<PlusIcon />}
            onClick={() => setIsAddModalOpen(true)}
            size="large"
            sx={{ whiteSpace: 'nowrap' }}
          >
            New Appointment
          </Button>
        )}
      </Box>

      {/* Left Section - Date navigation controls */}
      <Box
        sx={{
          gridArea: 'left',
          display: 'flex',
          gap: 2,
          alignItems: 'center',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {/* Today Button - Separate */}
          <Button
            onClick={handleToday}
            color="inherit"
            size="large"
            variant="outlined"
            sx={{
              color: '#535976',
              borderColor: 'rgba(0, 13, 77, 0.45)',
              '&:hover': { borderColor: '#535976' },
            }}
          >
            Today
          </Button>

          {/* Navigation Arrow Icons */}
          <Box sx={{ display: 'flex' }}>
            <IconButton
              onClick={handlePrevious}
              size="small"
              aria-label={`Go to previous ${viewMode}`}
              sx={{
                color: '#535976',
                padding: '4px',
                '&:hover': {
                  backgroundColor: 'transparent',
                  color: 'text.primary',
                },
              }}
            >
              <ChevronLeftIcon />
            </IconButton>
            <IconButton
              onClick={handleNext}
              size="small"
              aria-label={`Go to next ${viewMode}`}
              sx={{
                color: '#535976',
                padding: '4px',
                '&:hover': {
                  backgroundColor: 'transparent',
                  color: 'text.primary',
                },
              }}
            >
              <ChevronRightIcon />
            </IconButton>
          </Box>

          {/* Date Display */}
          <Typography
            sx={{
              fontSize: '18px',
              fontWeight: 400,
              color: '#535976',
              lineHeight: 'normal',
              whiteSpace: 'nowrap',
            }}
          >
            {getDateDisplay()}
          </Typography>
        </Box>
      </Box>

      {/* Center Section - View Controls - Only render on desktop */}
      {!isMobile && (
        <Box
          sx={{
            gridArea: 'center',
            display: 'flex',
            gap: 2,
            alignItems: 'center',
            justifyContent: {
              xs: 'flex-start',
              lg: 'center',
            },
            flexWrap: {
              xs: 'wrap',
              md: 'nowrap', // Prevent wrapping at md and above
            },
          }}
        >
          {/* View Mode Selector */}
          <ViewModeSelector variant="segmented" />

          {/* Schedule/List View Toggle - only on desktop */}
          <ScheduleViewToggle />
        </Box>
      )}

      {/* Right Section - Additional actions (only on desktop) */}
      {!isMobile && (
        <Box
          sx={{
            gridArea: 'right',
            display: 'flex',
            gap: 2,
            alignItems: 'center',
            justifyContent: {
              xs: 'flex-start',
              md: 'flex-end',
            },
          }}
        >
          <GroupBySelector viewMode={viewMode} />
          <BookingRequestsButton
            onToggle={() => setRequestsPanelOpen(!isRequestsPanelOpen)}
          />
          {hasReportsAccess && (
            <Tooltip
              title={
                isScheduleReportDisabled
                  ? 'Select a provider to download the schedule'
                  : 'Download schedule report'
              }
              placement="bottom"
              arrow={false}
            >
              {/* span wrapper keeps the tooltip working while the button is disabled */}
              <span>
                <IconButton
                  onClick={handleOpenScheduleReport}
                  disabled={isScheduleReportDisabled}
                  size="small"
                  aria-label="Download schedule report"
                  sx={{
                    color: '#535976',
                    '&:hover': { backgroundColor: 'rgba(83, 89, 118, 0.08)' },
                  }}
                >
                  <DownloadIcon fontSize="medium" />
                </IconButton>
              </span>
            </Tooltip>
          )}
          <CalendarSettingsMenu />
          {!isOrgScheduleActive && <SyncGoogleCalendar />}
        </Box>
      )}

      {/* Add Appointment Modal */}
      <AddAppointmentModal
        open={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onAppointmentCreated={handleAppointmentCreated}
      />

      {/* New Appointment FAB - only on mobile, hidden in read-only mode */}
      {isMobile && !isReadOnly && (
        <Zoom in={true} timeout={theme.transitions.duration.enteringScreen}>
          <Fab
            color="primary"
            aria-label="New Appointment"
            onClick={() => setIsAddModalOpen(true)}
            sx={{
              position: 'fixed',
              bottom: theme.spacing(3),
              right: theme.spacing(3),
              zIndex: 1200,
            }}
          >
            <PlusIcon />
          </Fab>
        </Zoom>
      )}
    </Box>
  );
};

export default CalendarNavigation;
